import { useRouter } from 'next/router';
import { ComponentType, Suspense, useEffect } from 'react';
import { logTraffic, useTrackingValues } from '@sso/traffic';
import dynamic from 'next/dynamic';
import { UrlParams } from '@sso/common-types';
import { BrandConfig } from '@sso/brand-configs';

import Head from '../../head';
import StickFooterGlobalStyle from '../../../components/styles/StickFooterGlobalStyle';
import WhiteBackgroundGlobalStyle from '../../../components/styles/WhiteBackgroundGlobalStyle';
import AccountCreateGlobalStyles from './styles/AccountCreateGlobalStyles';
import useAuthReactVariables from '../../../hooks/useAuthReactVariables';
import BackgroundPage from '../../../components/layout/BackgroundPage';

import { CONSENT_REQUIRED_MARKETS } from '@/constants/markets';
import { FormPage, FormPageColumn } from '@/components/layout/FormPage';
import { generateAccountCreateInitData } from '@/helpers/sso-init-data';
import usePromotionalBackground from '@/hooks/usePromotionalBackground';
import { MigrationData } from '@/hooks/useFetchMigrationData';

type Props = {
  emailCollectionTrafficPrefix: string;
  migrationData: Maybe<MigrationData>;
  featureFlags: FeatureFlags;
  isSignUpSsrOutsideIframe: boolean;
};

type CreateAccountPanelProps = {
  host: string;
  origin_app: string;
  origin_path: string;
  realm: string;
  market: string;
  version: string;
  plid: number;
  url_params: UrlParams;
  is_china: boolean;
  traffic_prefix: string;
  show_consent_collector: boolean;
  show_consent_button: boolean;
  complete_account: boolean;
  locale: Maybe<Record<string, string>>;
  brand_config: BrandConfig;
  migration_days_left: Maybe<number>;
  autofill_username: string;
  autofill_email: string;
  init_data: Maybe<{
    tos_url: string;
    is_china: boolean;
    privacy_url: string;
    go_back_url: string;
  }>;
  emailCollectionTrafficPrefix: string;
  isUserInSMSConsentExperimentForUS: boolean;
};

const AccountCreateView = dynamic(() => import('./AccountCreateView'), { ssr: true });

const CreateAccountPanel: ComponentType<CreateAccountPanelProps> = dynamic(
  () => import('auth-react/src/components/create-account-panel/CreateAccountPanel'),
  {
    ssr: false,
  },
);

const AccountCreatePage = ({
  migrationData,
  featureFlags = {},
  emailCollectionTrafficPrefix,
  isSignUpSsrOutsideIframe,
}: Props) => {
  const { plid, version, realm, market, authReactLocales, brandConfig, host, isChina } =
    useAuthReactVariables(featureFlags);
  const title = authReactLocales?.CREATE_ACCOUNT;
  const router = useRouter();
  const { query } = router;
  const isIframeMode = query.iframe === 'true';
  const { promoImage } = usePromotionalBackground();
  const smsConsentExperimentForUS = ['en_US', 'es_US'].includes(market);
  const consentShowButton = CONSENT_REQUIRED_MARKETS.includes(market) || smsConsentExperimentForUS;
  const initData = generateAccountCreateInitData(brandConfig, { host, urlParams: query, isChina });
  const nativeId = brandConfig?.native?.id;
  const trackingValues = useTrackingValues();
  const corrid = trackingValues?.corrid ?? '';

  useEffect(() => {
    if (nativeId != null) {
      logTraffic('load', `sso.create_account_panel.${nativeId.replaceAll('.', '_')}.page.load`);
    }
  }, [nativeId]);

  const { days_left: daysLeft, username, email, is_username_taken: isUsernameTaken } = migrationData ?? {};
  const autoFillUsername = Boolean(isUsernameTaken) === false && username != null ? username : '';
  const autoFillEmail = email != null ? email : '';

  return (
    <>
      <Head title={title} />
      <StickFooterGlobalStyle />
      <WhiteBackgroundGlobalStyle />
      <AccountCreateGlobalStyles />

      <BackgroundPage image={promoImage}>
        <FormPage iframeMode={isIframeMode} justify={promoImage !== null ? 'right' : 'center'}>
          <FormPageColumn fluid={isIframeMode} noPadding className='col-xs-12'>
            <div id='create-account-container'>
              {query.prefillEmail !== 'true' && isSignUpSsrOutsideIframe ? (
                <AccountCreateView
                  isChina={isChina}
                  corrid={corrid}
                  initData={initData}
                  showConsentCollector={consentShowButton}
                  showConsentButton={true}
                  isUserInSMSConsentExperimentForUS={smsConsentExperimentForUS}
                  completeAccount={false}
                  migrationDaysLeft={daysLeft}
                />
              ) : (
                <Suspense>
                  <CreateAccountPanel
                    host={host}
                    origin_app='sso'
                    origin_path='/account/create'
                    realm={realm}
                    market={market}
                    version={version}
                    plid={plid}
                    url_params={query as UrlParams}
                    is_china={isChina}
                    traffic_prefix='sso.create_account_panel'
                    show_consent_collector={consentShowButton}
                    show_consent_button={true}
                    complete_account={false}
                    locale={authReactLocales ?? {}}
                    migration_days_left={daysLeft}
                    autofill_username={autoFillUsername}
                    autofill_email={autoFillEmail}
                    init_data={initData}
                    brand_config={brandConfig}
                    emailCollectionTrafficPrefix={emailCollectionTrafficPrefix}
                    isUserInSMSConsentExperimentForUS={smsConsentExperimentForUS}
                  />
                </Suspense>
              )}
            </div>
          </FormPageColumn>
        </FormPage>
      </BackgroundPage>
    </>
  );
};

export default AccountCreatePage;
